import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { IngredientPicker } from "./IngredientPicker";
import { performStockAction } from "@/lib/stockSync";
import { EventType } from "@/lib/events";
import { R, fmtQty, rangeToDates, RangePicker, StatTile, ExplainerBox, EmptyState, ErrorState, errMsg, fmtWhen, humanize, useStockLocations, locationLabel } from "./shared";

const UNITS = ["kg", "g", "L", "ml", "pcs", "tots"];
// Mirrors WASTE_REASONS on the server; the report response also returns
// the authoritative list which overrides this once loaded.
const FALLBACK_REASONS = ["spoilage", "expired", "dropped", "burnt", "overproduction", "incorrect_preparation", "customer_complaint", "damaged", "staff_meal", "complimentary", "returned", "other"];
const EMPTY = { ingredient_id: "", quantity: "", unit: "", reason: "spoilage", location: "floor", notes: "" };

/**
 * Session 38 — Inventory → Waste.
 * Recording waste writes ONE ledger row (reason wastage/damaged/staff_meal/
 * complimentary/returned + `waste_reason`) and reduces the ingredient
 * atomically. The report groups those same rows — there is no second
 * "waste" collection.
 */
export const WastePanel = ({ http, authHeaders, ingredients = [], sessionUser, onStockChanged, sessionForEvents }) => {
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [range, setRange] = useState("7d");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [breakdown, setBreakdown] = useState("reason");
  // Session 39 — any configured stock location (server accepts id or legacy key).
  const stockLocs = useStockLocations(http, authHeaders);
  const locationOptions = useMemo(() => (stockLocs.active.length ? stockLocs.active.map((l) => ({ value: l.key || l.id, label: l.name })) : [{ value: "floor", label: "Floor / Kitchen" }, { value: "back_of_house", label: "Back of house" }]), [stockLocs.active]);

  const selectedIng = useMemo(() => ingredients.find((i) => i.id === draft.ingredient_id) || null, [ingredients, draft.ingredient_id]);
  const reasons = report?.reasons?.length ? report.reasons : FALLBACK_REASONS;

  const load = useCallback(async (rangeKey) => {
    setLoading(true);
    setError("");
    try {
      const r = await http.get("/owner/inventory/waste/report", { headers: authHeaders, params: rangeToDates(rangeKey || range) });
      setReport(r.data);
    } catch (e) {
      setError(errMsg(e, "Unable to load the waste report."));
    } finally {
      setLoading(false);
    }
  }, [http, authHeaders, range]);

  // Mount-only load (pickers/range changes call load() themselves).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(range); }, []);

  const previewValue = useMemo(() => {
    if (!selectedIng || !draft.quantity) return null;
    const cost = Number(selectedIng.cost_per_unit);
    if (!Number.isFinite(cost)) return null;
    const unit = draft.unit || selectedIng.unit;
    if (unit !== selectedIng.unit) return null; // server converts; only preview when same unit
    return Number(draft.quantity) * cost;
  }, [selectedIng, draft.quantity, draft.unit]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!draft.ingredient_id) { toast.error("Pick an ingredient."); return; }
    const qty = Number(draft.quantity);
    if (!Number.isFinite(qty) || qty <= 0) { toast.error("Enter a quantity greater than zero."); return; }
    setSaving(true);
    try {
      const body = {
        ingredient_id: draft.ingredient_id,
        quantity: qty,
        unit: draft.unit || null,
        reason: draft.reason,
        location: draft.location,
        notes: draft.notes.trim() || null,
      };
      // Session 40 — offline-safe: online → HTTP with a change_id; offline /
      // network failure → persisted to the local queue and synced later.
      const res = await performStockAction({ http, authHeaders, path: "/owner/inventory/waste", body, eventType: EventType.STOCK_WASTE_RECORDED, session: sessionForEvents || null });
      if (res.status === "queued") {
        toast.info(`Waste saved on this device — ${fmtQty(qty)} ${draft.unit || selectedIng?.unit || ""} ${selectedIng?.name || ""} will sync when the connection returns.`, { description: "Nothing is lost: the event is queued with its own id and can only be applied once." });
        setDraft((d) => ({ ...EMPTY, reason: d.reason, location: d.location }));
        return;
      }
      const rec = res.data?.recorded || {};
      toast.success(`Waste recorded: ${fmtQty(rec.quantity)} ${rec.unit} ${selectedIng?.name || ""}${rec.value !== null && rec.value !== undefined ? ` (${R(rec.value)})` : ""}`);
      if (res.data?.state === "negative") toast.warning(`${selectedIng?.name} is now in NEGATIVE stock — do a stock count.`);
      else if (res.data?.state === "out") toast.warning(`${selectedIng?.name} is now out of stock.`);
      setDraft((d) => ({ ...EMPTY, reason: d.reason, location: d.location }));
      onStockChanged?.();
      load(range);
    } catch (err) {
      toast.error(errMsg(err, "Unable to record waste."));
    } finally {
      setSaving(false);
    }
  };

  const groups = report ? { reason: report.by_reason, ingredient: report.by_ingredient, staff: report.by_staff, location: report.by_location, day: (report.by_day || []).map((d) => ({ key: d.date, ...d })) }[breakdown] || [] : [];
  const topReason = report?.by_reason?.[0];
  const topIng = report?.by_ingredient?.[0];

  return (
    <div className="space-y-3" data-testid="waste-panel">
      <ExplainerBox title="What is this tab?" testId="waste-explainer">
        <p>Log anything that left the shelf without being sold — spoilage, drops, burnt food, staff meals, comps. Each entry reduces stock immediately and is valued at the ingredient's cost, so the <strong>Variance</strong> tab can separate <em>explained</em> loss from what still needs investigating.</p>
      </ExplainerBox>

      <div className="grid min-w-0 gap-3 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
        <Card className="min-w-0 rounded-2xl border-border bg-card" data-testid="waste-form-card">
          <CardContent className="p-4">
            <p className="text-sm font-semibold">Record waste</p>
            <p className="mt-0.5 text-xs text-muted-foreground">Recorded by <span className="font-medium text-foreground">{sessionUser?.full_name || "Current user"}</span>.</p>
            <form className="mt-3 space-y-3" onSubmit={submit}>
              <div className="space-y-1.5">
                <Label className="text-xs">Ingredient</Label>
                <IngredientPicker ingredients={ingredients} value={draft.ingredient_id} onChange={(id, ing) => setDraft((d) => ({ ...d, ingredient_id: id, unit: ing?.unit || "" }))} testId="waste-ingredient" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="waste-qty">Quantity</Label>
                  <Input id="waste-qty" type="number" inputMode="decimal" min="0" step="any" value={draft.quantity} onChange={(e) => setDraft((d) => ({ ...d, quantity: e.target.value }))} className="h-11 rounded-xl tabular-nums" placeholder="0" data-testid="waste-quantity" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Unit</Label>
                  <Select value={draft.unit || selectedIng?.unit || "pcs"} onValueChange={(v) => setDraft((d) => ({ ...d, unit: v }))}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="waste-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Reason</Label>
                  <Select value={draft.reason} onValueChange={(v) => setDraft((d) => ({ ...d, reason: v }))}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="waste-reason"><SelectValue /></SelectTrigger>
                    <SelectContent>{reasons.map((r) => <SelectItem key={r} value={r}>{humanize(r)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Location</Label>
                  <Select value={draft.location} onValueChange={(v) => setDraft((d) => ({ ...d, location: v }))}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="waste-location"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {locationOptions.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`waste-location-option-${o.value}`}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs" htmlFor="waste-notes">Notes (optional)</Label>
                <Textarea id="waste-notes" value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} rows={2} className="rounded-xl" placeholder="e.g. fridge 2 door left open overnight" data-testid="waste-notes" />
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground" data-testid="waste-preview-value">{previewValue !== null ? `≈ ${R(previewValue)} at current cost` : selectedIng && !Number.isFinite(Number(selectedIng.cost_per_unit)) ? "No cost on this ingredient — value will show as —" : ""}</p>
                <Button type="submit" className="h-11 rounded-xl px-5" disabled={saving} data-testid="waste-submit">
                  {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />}Record waste
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <RangePicker value={range} onChange={(v) => { setRange(v); load(v); }} testIdPrefix="waste" />
            <Button type="button" size="sm" variant="outline" className="ml-auto rounded-xl" onClick={() => load(range)} disabled={loading} data-testid="waste-refresh">
              {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}Refresh
            </Button>
          </div>
          {error ? <ErrorState message={error} onRetry={() => load(range)} testId="waste-error" /> : null}
          {loading && !report ? (
            <div className="grid gap-3 sm:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
          ) : report ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile label="Waste value" value={R(report.total_value)} hint={`${report.count} entr${report.count === 1 ? "y" : "ies"}`} tone={Number(report.total_value) > 0 ? "bad" : "good"} testId="waste-tile-total" />
              <StatTile label="Top reason" value={topReason ? humanize(topReason.key) : "—"} hint={topReason ? R(topReason.value) : "Nothing logged"} tone="muted" testId="waste-tile-reason" />
              <StatTile label="Most wasted" value={topIng ? topIng.key : "—"} hint={topIng ? `${fmtQty(topIng.quantity)} ${topIng.unit || ""} · ${R(topIng.value)}` : "Nothing logged"} tone="muted" testId="waste-tile-ingredient" />
            </div>
          ) : null}

          <Card className="rounded-2xl border-border bg-card" data-testid="waste-breakdown-card">
            <CardContent className="p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold">Breakdown</p>
                <Tabs value={breakdown} onValueChange={setBreakdown}>
                  <TabsList className="h-8 rounded-xl">
                    {["reason", "ingredient", "staff", "location", "day"].map((k) => <TabsTrigger key={k} value={k} className="rounded-lg text-xs capitalize" data-testid={`waste-breakdown-${k}`}>{k}</TabsTrigger>)}
                  </TabsList>
                </Tabs>
              </div>
              {loading && !report ? <Skeleton className="h-24 w-full rounded-2xl" /> : groups.length ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="capitalize">{breakdown}</TableHead>
                        <TableHead className="text-right">Entries</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead className="hidden text-right sm:table-cell">Share</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groups.map((g) => (
                        <TableRow key={g.key} data-testid={`waste-group-${breakdown}-${g.key}`}>
                          <TableCell className="font-medium">{breakdown === "reason" ? humanize(g.key) : breakdown === "location" ? locationLabel(stockLocs.locations, g.key) : g.key}</TableCell>
                          <TableCell className="text-right text-xs tabular-nums">{g.count}</TableCell>
                          <TableCell className="text-right text-xs tabular-nums">{fmtQty(g.quantity)}{breakdown === "ingredient" && g.unit ? ` ${g.unit}` : ""}</TableCell>
                          <TableCell className="text-right text-xs font-semibold tabular-nums">{R(g.value)}</TableCell>
                          <TableCell className="hidden text-right text-xs tabular-nums sm:table-cell">{Number(report.total_value) > 0 ? `${((Number(g.value) / Number(report.total_value)) * 100).toFixed(0)}%` : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : report ? <EmptyState title="No waste logged in this period" body="That's either great news or nobody is recording it. Unrecorded waste shows up as unexplained variance." testId="waste-empty" /> : null}
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="rounded-2xl border-border bg-card" data-testid="waste-entries-card">
        <CardContent className="p-4">
          <p className="mb-3 text-sm font-semibold">Waste log</p>
          {loading && !report ? <Skeleton className="h-24 w-full rounded-2xl" /> : report?.entries?.length ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Ingredient</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="hidden md:table-cell">By</TableHead>
                    <TableHead className="hidden lg:table-cell">Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.entries.map((e) => (
                    <TableRow key={e.id} data-testid={`waste-entry-${e.id}`}>
                      <TableCell className="text-xs tabular-nums">{fmtWhen(e.created_at)}</TableCell>
                      <TableCell className="text-xs font-medium">{e.item_name}<Badge variant="outline" className="ml-1 rounded-full text-[9px]" data-testid={`waste-entry-location-${e.id}`}>{e.location_name || locationLabel(stockLocs.locations, e.location)}</Badge></TableCell>
                      <TableCell className="text-xs">{humanize(e.waste_reason)}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{fmtQty(e.quantity)} {e.unit}</TableCell>
                      <TableCell className="text-right text-xs font-semibold tabular-nums">{R(e.value)}</TableCell>
                      <TableCell className="hidden text-xs text-muted-foreground md:table-cell">{e.user || "—"}</TableCell>
                      <TableCell className="hidden max-w-[240px] truncate text-xs text-muted-foreground lg:table-cell">{e.notes || ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : report ? <p className="text-xs text-muted-foreground">No entries yet.</p> : null}
        </CardContent>
      </Card>
    </div>
  );
};
